/**
 * Section actions, end to end (M3.6b): compute the records a press implies,
 * append them to the log, queue the one event, and let the screen re-route
 * from the log until recomputation lands (M4.3).
 *
 * The order matters and is the honest one: nothing is queued until the
 * corrections are in the log, so a browser that refuses to write leaves no
 * event claiming otherwise. While the append is in flight the card says
 * "Recomputing" — that state is real, not a delay added for effect.
 */
import { create } from 'zustand'
import type { Correction, Ledger, LedgerItem, LedgerItemId, Party } from '@cordelia/core'
import { appendCorrection, listCorrections } from '../store/correctionLog.js'
import { db } from '../store/db.js'
import type { ActionSpec } from './catalog.js'
import { performAction, reopen, undoSuppression } from './perform.js'
import { recordAction, recordConfirmation } from './queue.js'
import type { LocalRouting } from './routing.js'

export type RunInput = {
  readonly spec: ActionSpec
  readonly item: LedgerItem
  readonly ledger: Ledger
  /** What the prompt collected, already resolved against the identity graph. */
  readonly party?: Party | undefined
}

export type ActionsState = {
  /** The whole log, so the overlay can be computed for every card. */
  readonly corrections: readonly Correction[]
  /** Cards whose append is in flight. */
  readonly recomputing: ReadonlySet<LedgerItemId>
  readonly hydrated: boolean
  /** Set when this browser refused a write. Nothing was recorded. */
  readonly error: string | null
  load(): Promise<void>
  run(input: RunInput): Promise<void>
  /** Takes back a press by appending its inverse. Never a delete, never an event. */
  undo(input: { item: LedgerItem; routing: LocalRouting }): Promise<void>
  /** After "Clear all data": the log is gone, so the overlay must be too. */
  clear(): void
}

const WRITE_FAILED = 'That did not save in this browser, so nothing was recorded.'

/** The same correction twice is refused, not duplicated: the log already holds it. */
async function append(correction: Correction): Promise<void> {
  try {
    await appendCorrection(correction)
  } catch (error) {
    if ((await db().corrections.get(correction.id)) === undefined) throw error
  }
}

export const useActions = create<ActionsState>((set, get) => ({
  corrections: [],
  recomputing: new Set(),
  hydrated: false,
  error: null,

  async load() {
    set({ corrections: await listCorrections(), hydrated: true })
  },

  async run({ spec, item, ledger, party }) {
    const outcome = performAction({ spec, item, ledger, party, time: new Date().toISOString() })
    // Correct hands off to the correction sheet (M4.1); it appends nothing here.
    if (outcome.opens_correction_sheet) return
    const busy = new Set(get().recomputing)
    if (outcome.corrections.length > 0) {
      busy.add(item.id)
      set({ recomputing: busy, error: null })
    }
    try {
      for (const correction of outcome.corrections) await append(correction)
      // One event per action, after the log, never before.
      if (outcome.confirmation) await recordConfirmation(outcome.confirmation)
      else if (outcome.product_action) await recordAction(outcome.product_action)
      set({ corrections: await listCorrections() })
    } catch {
      set({ error: WRITE_FAILED })
    } finally {
      const done = new Set(get().recomputing)
      done.delete(item.id)
      set({ recomputing: done })
    }
  },

  async undo({ item, routing }) {
    if (routing.undo === null) return
    const busy = new Set(get().recomputing)
    busy.add(item.id)
    set({ recomputing: busy, error: null })
    const time = new Date().toISOString()
    try {
      await append(
        routing.undo === 'unsuppress' && routing.suppression_id !== null
          ? undoSuppression({ item, suppression_id: routing.suppression_id, time })
          : reopen({ item, time }),
      )
      set({ corrections: await listCorrections() })
    } catch {
      set({ error: WRITE_FAILED })
    } finally {
      const done = new Set(get().recomputing)
      done.delete(item.id)
      set({ recomputing: done })
    }
  },

  clear() {
    set({ corrections: [], recomputing: new Set(), hydrated: false, error: null })
  },
}))
